import Vue from 'vue';
import Component from 'vue-class-component';
import { SCORM, UTILS } from 'pipwerks-scorm-api-wrapper';
import ConvertMilliSecondsIntoSCORM2004Time, {
  timeHandler
} from '@/utils/convertTimeScorm';
import Bookmark from '@/models/Bookmark.ts';
import { SCORM_CONST } from '@/constants/index';
import BTB_CONF from '@/btb-conf.json';
import ThemeConf from '@/types/ThemeConf';
import AccountInfo from '@/types/AccountInfo';

import { BtbConf } from '@/types/BtbConf';
import { Metadata } from '@/types/Metadata';
import axios, { AxiosPromise } from 'axios';
import store from '@/store';
import { getModule } from 'vuex-module-decorators';
import { ThemeConfModule } from '@/store/modules/theme';
import { AppModule } from '@/store/modules/app';
import {BTBA, FinishTrainingProperties} from '@/analytics/events/BTBA';
import Meta from '@/metadata.json'
import {inferScormLogsContext} from '@/airbreak.ts';

const AppState = getModule(AppModule, store);
const ThemeConf = getModule(ThemeConfModule, store);
@Component
export default class Scorm extends Vue {
  startTime!: Date;
  endTime!: Date;
  BTB_CONF: BtbConf = BTB_CONF;
  passingScore: number = ThemeConf.passingScore;
  metadata : Metadata = Meta
  public init() {
    // Overriding trace for SCORM logs.
    UTILS.trace = (error: string) => {
      this.scormErrorInterceptor(error);
      if(inferScormLogsContext(error) == 'error'){
        this.log('SCORM:', error);
      }
    };
    SCORM.connection.terminate = this.terminate;
    SCORM.quit = this.terminate;
    this.startTime = timeHandler.getStartTime;

    if (this.isScormless()) {
      this.log('Scormless setting','Package built as SCORMLESS.');
      return;
    }
    SCORM.init();
    this.log("SCORM API VERSION FETCHED", SCORM.version)
  }

  public setResults(score: number, total: number) {
    if (!this.isScormless()) {
      const scoreScaled = total > 0 ? score / total : 0;
      if (scoreScaled >= this.passingScore || total === 0) {
        this.setCompletion(SCORM_CONST.COMPLETE, true);
      } else {
        this.setCompletion(SCORM_CONST.INCOMPLETE, false);
      }
      this.setScore(total, score, scoreScaled);
    }

    this.trackFinish()
  };

  private getElapsedTime(): string {
    this.endTime = new Date();
    const elapsedTime = this.endTime.getTime() - this.startTime.getTime();
    const scormTime = ConvertMilliSecondsIntoSCORM2004Time(elapsedTime);
    return scormTime;
  }

  private get12ElapsedTime(): string {
    this.endTime = new Date();
    return this.get12ScormTile(this.startTime, this.endTime)
  }

  public get12ScormTile(startDate:Date, endDate:Date):string{
    if(startDate > endDate) [startDate, endDate] = [endDate, startDate]
    let elapsedTime = endDate.getTime() - startDate.getTime();
    let oneSecondInMil = 1000
    let oneMinuteInMil = oneSecondInMil*60
    let oneHoursInMil = oneMinuteInMil*60

    let addZeroIfLessThan10 = (val:number) => val<10 ? "0"+val.toFixed() : val.toFixed()
    let seconds = (elapsedTime/oneSecondInMil)%60
    elapsedTime = elapsedTime - seconds*oneSecondInMil
    let minutes = (elapsedTime/oneMinuteInMil)%60
    elapsedTime = elapsedTime - minutes*oneMinuteInMil
    let hours = (elapsedTime/oneHoursInMil)
    
    return `${addZeroIfLessThan10(hours)}:${addZeroIfLessThan10(minutes)}:${addZeroIfLessThan10(seconds)}`
  }




  public finish() {
    if (this.isScormless()) return;

    this.setSessionTime(this.getElapsedTime())
    SCORM.save();
    SCORM.quit();

  }

  public hasSCORMBookmark(): boolean {
    const bookmark = SCORM.get(SCORM_CONST.CMI_SUSPEND_DATA);
    let isBookmarkValid = this.isScormBookmarkValid(bookmark);
    this.logWithSuccessFlag(SCORM_CONST.CMI_SUSPEND_DATA, bookmark, isBookmarkValid)
    return isBookmarkValid;
  }

  public isScormBookmarkValid(bookmark: string = ''): boolean {
    return bookmark !== SCORM_CONST.NULL && bookmark.length > 0;
  }

  public versionHasChanged(bookmark: Bookmark) {
    const suspendDataMajorVersion = this.BTB_CONF.playerInfo.version.split(
      '.'
    )[0];
    const bkmrkMajorVersion = bookmark.version.split('.')[0];
    return suspendDataMajorVersion !== bkmrkMajorVersion;
  }

  public getBookmark(): Bookmark {
    const bookmarkString = SCORM.get(SCORM_CONST.CMI_SUSPEND_DATA);
    this.log(SCORM_CONST.CMI_SUSPEND_DATA, bookmarkString)

    let bookmark = this.getBookmarkFromString(bookmarkString);
    let location : number = this.getLocation()

    if (bookmark.currentIndex < location)
      bookmark.currentIndex = location 

    // We need to ignore Score if bookmark score does not reach the passing score
    if (bookmark.total != 0 && bookmark.score / bookmark.total <= ThemeConf.passingScore) {
      bookmark.score = 0
      bookmark.total = 0
    }

    return bookmark
  }
  public getLocation(): number {
    const location = SCORM.get(SCORM_CONST.CMI_LOCATION);
    this.log(SCORM_CONST.CMI_LOCATION, location)
    return location == '' ? 0 : Number(location) 
  }

  public getUserId(): string {
    if (this.isScormless()) return '';

    return SCORM.version === SCORM_CONST.V_1_DOT_2
      ? SCORM.get(SCORM_CONST.CMI_CORE_STUDENT_ID)
      : SCORM.get(SCORM_CONST.CMI_LEARNER_ID);
  }

  public getBookmarkFromString(bookmark: string = ''): Bookmark {
    try {
      if (this.isScormBookmarkValid(bookmark)) {
        let bookmarkObj: Bookmark = JSON.parse(bookmark);
        bookmarkObj = new Bookmark(
          bookmarkObj.currentIndex,
          bookmarkObj.score,
          bookmarkObj.total,
          bookmarkObj.biggestIndex,
          bookmarkObj.version,
          bookmarkObj.lastSlideState
        );
        // TODO: This is regarding BTB-583. Still has to come to a decision
        // if (this.versionHasChanged(bookmarkObj))
        //   return new Bookmark(0, 0, 0, 0, this.BTB_CONF.playerInfo.version);
        if (bookmarkObj.biggestIndex === undefined)
          bookmarkObj.biggestIndex = bookmarkObj.currentIndex;
        return bookmarkObj;
      }
    } catch (err) {
      console.log('Failed to load suspend data. Reseting');
    }
    return new Bookmark(0, 0, 0, 0, this.BTB_CONF.playerInfo.version);
  }

  public setBookmark(bookmark: Bookmark) {
    if (this.isScormless()) return;

    bookmark.elapsedtime = this.getElapsedTime();
    this.setSessionTime(this.getElapsedTime())
    this.log('Attempting to save bookmark', bookmark)
    let bookmarkSucceeded = SCORM.set(SCORM_CONST.CMI_SUSPEND_DATA, JSON.stringify(bookmark));
    this.logWithSuccessFlag(SCORM_CONST.CMI_SUSPEND_DATA, JSON.stringify(bookmark), bookmarkSucceeded);
    
    let locationSucceeded = SCORM.set(SCORM_CONST.CMI_LOCATION, bookmark.currentIndex);
    this.logWithSuccessFlag(SCORM_CONST.CMI_LOCATION, bookmark.currentIndex, locationSucceeded);
    
    SCORM.save();
  }

  public setSessionTime(sessionTime:string){
    if(SCORM.version === SCORM_CONST.V_1_DOT_2){
      sessionTime = this.get12ElapsedTime()
      SCORM.set(SCORM_CONST.CMI_CORE_SESSION_TIME, sessionTime);
      this.log(SCORM_CONST.CMI_CORE_SESSION_TIME, sessionTime);
    }else{
      SCORM.set(SCORM_CONST.CMI_SESSION_TIME, sessionTime);
      this.log(SCORM_CONST.CMI_SESSION_TIME, sessionTime);
    }
  }

  public getThemeConf(): ThemeConf {
    // just mocking the request
    // This brakes the test, as it adds a new first slide. And the tests are not adjusted
    // TODO: change here to test locally
    // const themeConf: ThemeConf = JSON.parse(
    //   '{"color_primary": "#006721","logo": "https://placekitten.com/500/300", "light_mode_allowed":true, "dark_mode_allowed":true, "default_mode":"light", "slides":{"intro": {"image": "https://placekitten.com/512/768","heading": "Welcome to the Course","subheading": "We built a great course, and nobody builds courses better than us. People tell us all the time that these are great, great courses."}, "outro": {"image": "https://placekitten.com/512/768","heading": "That Was the Course","subheading": "Of course it was a course, full of discourse. You had no recourse but to finish the course on this course."}},"contact": {"email": "Email: AcmeCorpAcmeCorpAcmeCorpAcmeCorpAcmeCorpAcmeCorpAcmeCorpAcmeCorpAcmeCorpAcmeCorpAcmeCorp@email.com","phone": "1 1337 8008"}, "social_media": [{"link": "https://twitter.com/SwiftOnSecurity","icon": "fa-twitter", "desc":"Twitter"},{"link": "https://facebook.com/SwiftOnSecurity","icon": "fa-facebook", "desc":"Facebook"},{"link": "https://youtube.com/SwiftOnSecurity","icon": "fa-youtube", "desc":"Youtube"}]}'
    // );
    // return themeConf;
    try {
      const themeConf: ThemeConf = JSON.parse(SCORM.get(SCORM_CONST.KB4_THEME));
      this.log(SCORM_CONST.KB4_THEME, themeConf)
      return themeConf;
    } catch (err) {
      console.log('Failed to load theme');
      return {};
    }
  }

  public getAccountInfo(): AccountInfo | null  {
    try {
      const accountInfo: AccountInfo = JSON.parse(SCORM.get(SCORM_CONST.KB4_ACCOUNT_INFO));
      this.log(SCORM_CONST.KB4_ACCOUNT_INFO, accountInfo)
      return accountInfo;
    } catch (err) {
      console.log('Failed to load Account Info');
      return null
    }
  }

  public getAccountInfoFile(): AxiosPromise<AccountInfo>{
    return axios.get(`account_info.json`);

  }

  public getThemeFile(): AxiosPromise<ThemeConf> {
    return axios.get(`theme.json`);
  }

  public onLostConnection(error?: string) {
    if (this.isScormless()) return;

    console.log('Connection Lost');
  }

  private setScore(total: number, score: number, scoreScaled: number) {
    if (SCORM.version === SCORM_CONST.V_1_DOT_2) {
      let successMinCall = SCORM.set(SCORM_CONST.CMI_CORE_SCORE_MIN, '0');
      this.logWithSuccessFlag(SCORM_CONST.CMI_CORE_SCORE_MIN, '0', successMinCall);

      let successMaxCall = SCORM.set(SCORM_CONST.CMI_CORE_SCORE_MAX, '100');
      this.logWithSuccessFlag(SCORM_CONST.CMI_CORE_SCORE_MAX, '100', successMaxCall);

      let scoreScaledString = (scoreScaled*100).toString()
      let successRawCall = SCORM.set(SCORM_CONST.CMI_CORE_SCORE_RAW, scoreScaledString);
      this.logWithSuccessFlag(SCORM_CONST.CMI_CORE_SCORE_RAW, scoreScaledString, successRawCall);
    } else {
      SCORM.set(SCORM_CONST.CMI_SCORE_MIN, '0');
      SCORM.set(SCORM_CONST.CMI_SCORE_MAX, total.toString());

      let scoreString = score.toString()
      let successRawCall = SCORM.set(SCORM_CONST.CMI_SCORE_RAW, scoreString);
      this.logWithSuccessFlag(SCORM_CONST.CMI_SCORE_RAW, scoreString, successRawCall);

      let scoreScaledString = scoreScaled.toString()
      let successScaledCall = SCORM.set(SCORM_CONST.CMI_SCORE_SCALED, scoreScaledString);
      this.logWithSuccessFlag(SCORM_CONST.CMI_SCORE_SCALED, scoreScaledString, successScaledCall);
    }
    let saveSucceded = SCORM.save();
    this.log('save', saveSucceded)
  }

  private setCompletion(status: string, hasPassed: boolean) {
    if (SCORM.version === SCORM_CONST.V_1_DOT_2) {
      let passingFlag = this.getScorm12PassingFlag(hasPassed);
      let succesLessonStatusCall = SCORM.set(SCORM_CONST.CMI_CORE_LESSON_STATUS, passingFlag);
      this.logWithSuccessFlag(SCORM_CONST.CMI_CORE_LESSON_STATUS, passingFlag, succesLessonStatusCall);
    } else {
      let successComletionCall = SCORM.set(SCORM_CONST.CMI_COMPLETION_STATUS, status);
      this.logWithSuccessFlag(SCORM_CONST.CMI_COMPLETION_STATUS, status, successComletionCall);

      let passingFlag = hasPassed ? SCORM_CONST.PASSED : SCORM_CONST.FAILED;
      let successStatusCall = SCORM.set(SCORM_CONST.CMI_SUCCESS_STATUS, passingFlag);
      this.logWithSuccessFlag(SCORM_CONST.CMI_SUCCESS_STATUS, passingFlag, successStatusCall);
    }

    let saveSucceded = SCORM.save();
    this.log('save', saveSucceded)
  }
  private scormErrorInterceptor(error: string) {
    if (
      error.indexOf("Can't find the API") > -1 ||
      error.indexOf('API connection is inactive') > -1
    ) {
      this.onLostConnection(error);
    }
  }


  public log(logType: string, object: any) {
    if (this.BTB_CONF.debug) {
      console.log('=== LOG ===');
      console.log(`TYPE: ${logType}`);
      console.log('VALUE:', object);
    }
  }

  public logWithSuccessFlag(logType: string, object: any, success: boolean) {
    if (this.BTB_CONF.debug) {
      this.log(logType, object)
      console.log('CALL SUCCESS:', success);
    }
  }

  public isScormless () {
    return (BTB_CONF as BtbConf).scormless
  }

  public closeNav() {
    SCORM.set(SCORM_CONST.ADL_NAV_REQUEST, SCORM_CONST.EXIT_ALL);
    this.finish()
  }

  public terminate (){
    var success = false,
      scorm = SCORM,
      exitStatus = scorm.data.exitStatus,
      completionStatus = scorm.data.completionStatus,
      trace = UTILS.trace,
      makeBoolean = UTILS.StringToBoolean,
      debug = scorm.debug,
      traceMsgPrefix = "SCORM.connection.terminate ";
    console.log('new Terminate used!')
    if (scorm.connection.isActive) {
      var API = scorm.API.getHandle(),
        errorCode = 0;
      if (API) {
        if (scorm.handleExitMode && !exitStatus) {
          if (completionStatus !== "completed" && completionStatus !== "passed") {
            switch (scorm.version) {
              case "1.2": success = scorm.set("cmi.core.exit", "suspend"); break;
              case "2004": success = scorm.set("cmi.exit", "suspend"); break;
            }
          } else {
            switch (scorm.version) {
              case "1.2": success = scorm.set("cmi.core.exit", ""); break;
              case "2004": success = scorm.set("cmi.exit", "normal"); break;
            }
          }
        }
        //Ensure we persist the data
        success = scorm.save();
        if (success) {
          switch (scorm.version) {
            case "1.2": success = makeBoolean(API.LMSFinish("")); break;
            case "2004": success = makeBoolean(API.Terminate("")); break;
          }
          if (success) {
            scorm.connection.isActive = false;
          } else {
            errorCode = debug.getCode();
            trace(traceMsgPrefix + "failed. \nError code: " + errorCode + " \nError info: " + debug.getInfo(errorCode));
          }
        }
      } else {
        trace(traceMsgPrefix + "failed: API is null.");
      }
    } else {
      trace(traceMsgPrefix + "aborted: Connection already terminated.");
    }
    return success;
  };

  private trackFinish() {
    this.$analytics.trackEvent(BTBA.finishTraining, {
      'Session Duration': AppState.getTrainingTimer.getTotalTimeValues().seconds,
      'Success Status ': AppState.successStatus,
      'Complete Status': AppState.completionStatus,
      'Final Score': AppState.getScore,
      'Minimum Score': 0,
      'Maximum Score': AppState.getTotalScore,
      'Scaled Score': AppState.getScaledScore,
      'Is Sidebar Opened': AppState.sidebarOpen,
      'Current Page': AppState.currentSlide,
      'Page ID': AppState.getCurrentSlide,
      'Page Title': AppState.getCurrentSlideTitle
    } as FinishTrainingProperties)
  }


  public getScorm12PassingFlag(hasPasses : boolean) :string{
    let srf = ThemeConf.scormResultFormat || this.metadata.scorm_result_format
    this.log('Passing Flags (scorm_1.2)', srf)
    switch(srf){
      case "P/I":
        return hasPasses ? SCORM_CONST.PASSED : SCORM_CONST.INCOMPLETE
      case "C/I":
        return hasPasses ? SCORM_CONST.COMPLETE : SCORM_CONST.INCOMPLETE
      case "C/F":
        return hasPasses ? SCORM_CONST.COMPLETE : SCORM_CONST.FAILED
      case "P/F":
      default:
        return hasPasses ? SCORM_CONST.PASSED : SCORM_CONST.FAILED
    }
  }


}
